import { UserPlace, UserService } from '@geovelo-frontends/commons';
import { Close } from '@mui/icons-material';
import { Box, CircularProgress, DialogProps, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContext } from '../app/context';
import { JourneyCard } from '../pages/profile/journeys-card';

import { Button } from './button';
import { Dialog } from './dialog';
import { RemoveIcon } from './icons';

function RemovePlaceDialog({
  place,
  ...props
}: Omit<DialogProps, 'onClose'> & {
  place: UserPlace;
  onClose: (removed?: boolean) => void;
}): React.JSX.Element {
  const [loading, setLoading] = useState(false);
  const {
    user: { works, commutingTrips },
    actions: { setUserHome, setUserWorks, setUserCommutingTrips },
  } = useContext(AppContext);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  async function handleRemove() {
    if (!place.id || typeof place.id !== 'number') return;

    setLoading(true);

    try {
      if (place.id !== -1) await UserService.removePlace(place.id);

      if (place.type === 'home') setUserHome(null);
      else if (place.type === 'work' && works) {
        setUserWorks(works.slice().filter(({ id }) => id !== place.id));

        const commutingTripIndex =
          commutingTrips?.findIndex(({ work }) => work.id === place.id) || -1;
        if (commutingTrips && commutingTripIndex > -1) {
          const newCommutingTripsTrips = [...commutingTrips];
          newCommutingTripsTrips.splice(commutingTripIndex, 1);
          setUserCommutingTrips(newCommutingTripsTrips);
        }
      }

      enqueueSnackbar("L'adresse a bien été supprimée");
      props.onClose(true);
    } catch {
      enqueueSnackbar("L'adresse n'a pas pu être supprimée", {
        variant: 'error',
      });
    }

    setLoading(false);
  }

  const commutingTrip =
    (place.type === 'work' && commutingTrips?.find(({ work }) => work.id === place.id)) || null;

  return (
    <Dialog fullWidth loading={loading} maxWidth="sm" {...props}>
      <Box display="flex" flexDirection="column" gap={3}>
        <Typography fontSize="1.25rem" fontWeight={600}>
          {commutingTrip
            ? 'Supprimer cette adresse et le trajet vélotaf associé ?'
            : 'Supprimer cette adresse ?'}
        </Typography>
        <Typography component="div" variant="body2">
          {commutingTrip
            ? `Vous vous apprêtez à supprimer cette adresse de travail et le trajet vélotaf associé. Attention, cette action est définitive et irréversible.`
            : `Vous vous apprêtez à supprimer cette adresse. Attention, cette action est définitive et irréversible.`}
        </Typography>
        {commutingTrip && <JourneyCard disableActions commutingTrip={commutingTrip} />}
        <Box alignItems="center" display="flex" gap={3} justifyContent="flex-end">
          <Button
            color="primary"
            disabled={loading}
            onClick={() => props.onClose()}
            startIcon={<Close />}
            variant="outlined"
          >
            {t('commons.actions.cancel')}
          </Button>
          <Button
            color="error"
            disabled={loading}
            onClick={handleRemove}
            startIcon={
              loading ? (
                <CircularProgress color="inherit" size={16} thickness={4} />
              ) : (
                <RemoveIcon />
              )
            }
            variant="contained"
          >
            {t('commons.actions.remove')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

export default RemovePlaceDialog;
